import { readFileSync, writeFileSync, mkdirSync, unlinkSync } from 'node:fs';
import path from 'node:path';
import { execSync } from 'node:child_process';
import Handlebars from 'handlebars';
import { fileURLToPath } from 'node:url';

const __filename = fileURLToPath(import.meta.url);
const __dirname = path.dirname(__filename);

export interface GenerateOptions {
  swaggerPath: string;
  routesDir: string;
  esm: boolean;
}

interface OperationParam {
  name: string;
  in: string;
  required?: boolean;
  schema?: any;
}

interface ToolOperation {
  operationId: string;
  method: string;
  path: string;
  params: OperationParam[];
}

/**
 * Generate MCP routes file:
 * 1. Run Orval to generate Zod schemas from swagger.json
 * 2. Parse swagger.json to map operations to their Orval schema names
 * 3. Render routes.mcp.hbs template
 * 4. Write routes.mcp.ts
 */
export async function generateMcpRoutes(opts: GenerateOptions): Promise<void> {
  const { swaggerPath, routesDir, esm } = opts;
  const ext = esm ? '.js' : '';

  mkdirSync(routesDir, { recursive: true });

  const orvalOutputPath = path.join(routesDir, 'mcp.schemas');
  const orvalConfigPath = path.join(routesDir, '_orval.config.mjs');

  // Step 1: Write temp Orval config and run Orval CLI
  console.log('[tsoa-mcp] Running Orval (zod client)...');

  const orvalConfig = `
export default {
  'mcp-schemas': {
    input: { target: '${swaggerPath.replace(/\\/g, '/')}' },
    output: {
      target: '${orvalOutputPath.replace(/\\/g, '/')}.ts',
      client: 'zod',
      mode: 'single',
    },
  },
};
`;

  writeFileSync(orvalConfigPath, orvalConfig, 'utf-8');

  try {
    // Resolve orval binary: import.meta.resolve gives .../dist/index.mjs
    // Go up to package root, then into dist/bin/orval.mjs
    const orvalEntry = import.meta.resolve('orval').replace('file://', '');
    const orvalPkgDir = path.dirname(path.dirname(orvalEntry)); // up from dist/index.mjs -> pkg root
    const orvalBin = path.join(orvalPkgDir, 'dist', 'bin', 'orval.mjs');
    execSync(`node "${orvalBin}" --config "${orvalConfigPath}"`, {
      stdio: 'inherit',
      cwd: process.cwd(),
    });
  } finally {
    // Clean up temp config
    try { unlinkSync(orvalConfigPath); } catch { /* ignore */ }
  }

  // Step 2: Parse swagger.json to extract all operations
  const swagger = JSON.parse(readFileSync(swaggerPath, 'utf-8'));
  const operations = extractOperations(swagger);

  console.log(`[tsoa-mcp] Found ${operations.length} operations in swagger.json`);

  // Step 3: Build template data
  const templateData = {
    operations: operations.map((op) => ({
      operationId: op.operationId,
      queryParamsSchema: hasQueryParams(op) ? `${op.operationId}QueryParams` : null,
      pathParamsSchema: hasPathParams(op) ? `${op.operationId}Params` : null,
      bodySchema: hasBody(op) ? `${op.operationId}Body` : null,
      headerSchema: hasHeaderParams(op) ? `${op.operationId}Header` : null,
    })),
    schemasImportPath: `./mcp.schemas${ext}`,
    routesImportPath: `./routes${ext}`,
    ext,
  };

  // Step 4: Render HBS template
  const templatePath = path.resolve(__dirname, '..', 'template', 'routes.mcp.hbs');
  const templateSource = readFileSync(templatePath, 'utf-8');
  const template = Handlebars.compile(templateSource);
  const output = template(templateData);

  // Step 5: Write routes.mcp.ts
  const outputPath = path.join(routesDir, 'routes.mcp.ts');
  writeFileSync(outputPath, output, 'utf-8');
  console.log(`[tsoa-mcp] Generated ${outputPath}`);
}

function extractOperations(swagger: any): ToolOperation[] {
  const operations: ToolOperation[] = [];
  const paths = swagger.paths || {};

  for (const [routePath, methods] of Object.entries<any>(paths)) {
    for (const [method, operation] of Object.entries<any>(methods)) {
      if (!operation.operationId) continue;

      const params: OperationParam[] = [];

      if (Array.isArray(operation.parameters)) {
        for (const param of operation.parameters) {
          params.push({
            name: param.name,
            in: param.in,
            required: param.required,
            schema: param.schema,
          });
        }
      }

      if (operation.requestBody) {
        params.push({
          name: '__body',
          in: 'body',
          required: operation.requestBody.required,
          schema: operation.requestBody.content?.['application/json']?.schema,
        });
      }

      operations.push({
        operationId: operation.operationId,
        method,
        path: routePath,
        params,
      });
    }
  }

  return operations;
}

function hasQueryParams(op: ToolOperation): boolean {
  return op.params.some((p) => p.in === 'query');
}

function hasPathParams(op: ToolOperation): boolean {
  return op.params.some((p) => p.in === 'path');
}

function hasBody(op: ToolOperation): boolean {
  return op.params.some((p) => p.in === 'body');
}

function hasHeaderParams(op: ToolOperation): boolean {
  return op.params.some((p) => p.in === 'header');
}
